// =============================================================================
// Pintravel — Profile numbers
// =============================================================================
// The three counts under the avatar. They were literal zeroes in the JSX until
// now, which read as "you have done nothing" on an account with eleven trips.
// =============================================================================

import type { FollowRelation } from '@itravel/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api/instance';
import { useAuthStore } from '@/store/auth.store';

export interface ProfileStats {
  trips: number;
  /** Travel points marked visited — not points planned. */
  places: number;
}

/** What anyone signed in may see about anyone else. */
export interface PublicProfile {
  id: string;
  /** The Handle; null for a guest. */
  username: string | null;
  displayName: string;
  avatarUrl: string | null;
  bio: string | null;
  createdAt: string;
  isPrivate: boolean;
  followersCount: number;
  followingCount: number;
  /** self | none | following | requested — the button on the profile. */
  relation: FollowRelation;
  /** False on a private account the viewer does not follow: grid and Boards stay hidden. */
  canSeeContent: boolean;
  stats: ProfileStats;
}

export const profileKeys = {
  all: ['profile'] as const,
  stats: () => [...profileKeys.all, 'stats'] as const,
  public: (userId: string) => [...profileKeys.all, 'public', userId] as const,
};

export function usePublicProfile(userId: string | undefined) {
  return useQuery({
    queryKey: profileKeys.public(userId ?? ''),
    queryFn: async (): Promise<PublicProfile> => {
      const response = await api.get(`users/${userId}/profile`).json<{ data: PublicProfile }>();
      return response.data;
    },
    enabled: Boolean(userId),
    staleTime: 60_000,
  });
}

/** The same profile, by Handle — what /u/<handle> shows. */
export function usePublicProfileByHandle(handle: string | undefined) {
  return useQuery({
    queryKey: [...profileKeys.all, 'handle', handle ?? ''] as const,
    queryFn: async (): Promise<PublicProfile> => {
      const response = await api
        .get(`users/handle/${encodeURIComponent(handle ?? '')}/profile`)
        .json<{ data: PublicProfile }>();
      return response.data;
    },
    enabled: Boolean(handle),
    staleTime: 60_000,
  });
}

export function useProfileStats(enabled = true) {
  return useQuery({
    queryKey: profileKeys.stats(),
    queryFn: async (): Promise<ProfileStats> => {
      const response = await api.get('users/me/stats').json<{ data: ProfileStats }>();
      return response.data;
    },
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Changes the Handle. The signed-in user lives in the auth store, so the new
 * handle is written there on success — nothing else re-reads `auth/me`.
 */
export function useUpdateMyProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (changes: {
      username?: string;
      isPrivate?: boolean;
    }): Promise<PublicProfile> => {
      const response = await api
        .patch('users/me/profile', { json: changes })
        .json<{ data: PublicProfile }>();
      return response.data;
    },
    onSuccess: (profile) => {
      useAuthStore.getState().updateUser({ username: profile.username });
      queryClient.setQueryData(profileKeys.public(profile.id), profile);
    },
  });
}
